/**
 * Pure Age Group derivation (S1.1) — xlsx C108 inline 【MUST】: the user
 * enters the vehicle start/end year (車子起始年分 / 車子終止年分) and the
 * SYSTEM computes which AGE GROUP (車齡組別) the product belongs to. The
 * field is never directly enterable (registry marks `ageGroup`
 * `systemAssigned`; handlers ignore a caller-sent value).
 *
 * Pure module contract (the `resolvePrice.ts` precedent): no I/O, no
 * globals, no Date.now — the caller supplies `asOfYear`, so seed, handler
 * and UI can never drift.
 *
 * Band VALUES are BUILD-DEFAULT constants pending the owner ruling (plan
 * §S1.1: "build-default band constants, values = Q-O4"); a ruling is a
 * one-line edit to `AGE_GROUP_BANDS`. Classification basis (also
 * build-default, documented for the ruling): the vehicle age of the
 * MIDPOINT of the covered model-year range, clamped at 0 (future model
 * years count as age 0) — the midpoint is the most representative age of
 * the fleet the product serves. Example: a 2015–2023 fitment as of 2026 has
 * midpoint 2019 → vehicle age 7 → band `4-7`.
 */

/** Stable band code — language-neutral, so it reads as-is until the S2.1 chip lands i18n. */
export type AgeGroupCode = '0-3' | '4-7' | '8-12' | '13+';

export interface AgeGroupBand {
  /** Stable code (doubles as the raw display fallback). */
  code: AgeGroupCode;
  /** Inclusive lower vehicle-age bound, in years. */
  minAge: number;
  /** Inclusive upper vehicle-age bound; `null` = open-ended. */
  maxAge: number | null;
}

/**
 * BUILD-DEFAULT band constants (values = Q-O4 pending ruling). Ordered
 * ascending; the last band is open-ended so every non-negative age maps to
 * exactly one band.
 */
export const AGE_GROUP_BANDS: readonly AgeGroupBand[] = [
  { code: '0-3', minAge: 0, maxAge: 3 },
  { code: '4-7', minAge: 4, maxAge: 7 },
  { code: '8-12', minAge: 8, maxAge: 12 },
  { code: '13+', minAge: 13, maxAge: null },
] as const;

/** Sanity bounds for user-entered model years (build-default guard). */
const MIN_MODEL_YEAR = 1900;
const MAX_MODEL_YEAR = 9999;

/**
 * Map a non-negative vehicle age (years, fractional allowed — midpoints of
 * even spans land on x.5) onto its band. Exported for the S2.1 chip legend.
 */
export function ageGroupBandFor(age: number): AgeGroupBand | undefined {
  if (!Number.isFinite(age) || age < 0) return undefined;
  return AGE_GROUP_BANDS.find((band) => band.maxAge === null || age <= band.maxAge);
}

/**
 * C108 — compute the Age Group from the user-entered vehicle year range.
 *
 * Returns `undefined` (no group) when the inputs cannot produce one:
 * missing/non-integer years, `startYear > endYear`, or years outside the
 * plausible model-year window. Pure and total — safe to call with raw
 * optional `Product` fields.
 */
export function computeAgeGroup(
  startYear: number | null | undefined,
  endYear: number | null | undefined,
  asOfYear: number
): AgeGroupCode | undefined {
  if (
    typeof startYear !== 'number' ||
    typeof endYear !== 'number' ||
    !Number.isInteger(startYear) ||
    !Number.isInteger(endYear) ||
    !Number.isInteger(asOfYear)
  ) {
    return undefined;
  }
  if (startYear > endYear) return undefined;
  if (startYear < MIN_MODEL_YEAR || endYear > MAX_MODEL_YEAR) return undefined;

  const midpointYear = (startYear + endYear) / 2;
  const age = Math.max(0, asOfYear - midpointYear);
  return ageGroupBandFor(age)?.code;
}
